import enviro from 'enviro';
import http from 'hub-http/clients/apiClient';
import PortalIdParser from 'PortalIdParser';
import { useCallback, useMemo } from 'react';
import { registerMutation, useMutation } from 'academy-common-lib/utils/DataFetchingClient';
import { CREATE_PORTAL_MUTATION } from '../../../__generated__/chirp/com/hubspot/academy/simulation/environment/rpc/PortalService.dfc';
import { useGetRequirementsQuery } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/ExerciseChirpService.hooks';
import { useHasGate } from '../accounts/accountsQueries';
import { deleteContentProgressFromCache, setContentProgressStatusCache } from '../userProgress/userProgressQueries';
import { BASE_URI_PROGRESS } from '../../constants/ApiConstants';
const EXERCISE_SUBMISSIONS_URI = `${BASE_URI_PROGRESS}/exercise-submissions`;
export const usePracticalExerciseRequirements = variables => {
  const {
    data: practicalExerciseRequirements,
    loading: practicalExerciseRequirementsLoading,
    error: practicalExerciseRequirementsError
  } = useGetRequirementsQuery({
    variables
  });
  return {
    practicalExerciseRequirements,
    practicalExerciseRequirementsLoading,
    practicalExerciseRequirementsError
  };
};
const START_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'setPracticalExerciseStarted',
  args: ['contentId', 'trackId', 'lessonId', 'simulationPortalId'],
  fetcher({
    contentId,
    lessonId,
    trackId,
    simulationPortalId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/exercise-submissions/${contentId}/asset/start`, {
      data: {
        trackId,
        lessonId,
        simulationPortalId
      }
    });
  }
});
export const useSetPracticalExerciseStarted = () => {
  const [mutate, {
    loading: startPracticalExerciseLoading,
    called: startPracticalExerciseCalled,
    error: startPracticalExerciseError
  }] = useMutation(START_PRACTICAL_EXERCISE);
  const startExercise = useCallback((lessonId, contentId, trackId, simulationPortalId) => {
    return mutate({
      variables: {
        contentId,
        lessonId,
        trackId,
        simulationPortalId
      },
      update() {
        setContentProgressStatusCache({
          trackId,
          lessonId,
          contentId,
          status: 'STARTED'
        });
      }
    }).then(result => result.data);
  }, [mutate]);
  return [startExercise, {
    startPracticalExerciseLoading,
    startPracticalExerciseCalled,
    startPracticalExerciseError
  }];
};
const DELETE_PRACTICAL_EXERCISE_PROGRESS = registerMutation({
  fieldName: 'deletePracticalExerciseProgress',
  args: ['contentId'],
  fetcher({
    contentId
  }) {
    return http.delete(`${BASE_URI_PROGRESS}/user-progress/exercise/${contentId}`);
  }
});
export const useDeletePracticalExerciseProgress = () => {
  const [mutate, {
    loading: deletePracticalExerciseProgressLoading,
    called: deletePracticalExerciseProgressCalled,
    error: deletePracticalExerciseProgressError
  }] = useMutation(DELETE_PRACTICAL_EXERCISE_PROGRESS);
  const deleteExerciseProgress = useCallback(({
    contentId,
    lessonId,
    trackId
  }) => {
    return mutate({
      variables: {
        contentId
      },
      update() {
        deleteContentProgressFromCache({
          contentId,
          lessonId,
          trackId
        });
      }
    }).then(response => response.data);
  }, [mutate]);
  return [deleteExerciseProgress, {
    deletePracticalExerciseProgressLoading,
    deletePracticalExerciseProgressCalled,
    deletePracticalExerciseProgressError
  }];
};
const SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'submitManuallyGradedPracticalExercise',
  args: ['contentId', 'language', 'trackId', 'lessonId'],
  fetcher({
    contentId,
    language,
    trackId,
    lessonId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/exercise-submissions/${contentId}/asset/manual`, {
      data: {
        trackId,
        lessonId,
        language
      }
    });
  }
});
export const useSetManuallyGradedPracticalExerciseSubmitted = () => {
  const [mutate, {
    loading: submitManuallyGradedPracticalExerciseLoading,
    called: submitManuallyGradedPracticalExerciseCalled,
    error: submitManuallyGradedPracticalExerciseError
  }] = useMutation(SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE);
  const submitManuallyGradedExercise = useCallback(({
    contentId,
    trackId,
    lessonId
  }) => {
    return mutate({
      variables: {
        contentId,
        trackId,
        lessonId
      },
      update() {
        setContentProgressStatusCache({
          trackId,
          lessonId,
          contentId,
          status: 'IN_REVIEW'
        });
      }
    }).then(response => response.data);
  }, [mutate]);
  return [submitManuallyGradedExercise, {
    submitManuallyGradedPracticalExerciseLoading,
    submitManuallyGradedPracticalExerciseCalled,
    submitManuallyGradedPracticalExerciseError
  }];
};
const SUBMIT_AUTOMATICALLY_GRADED_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'submitAutomaticallyGradedPracticalExercise',
  args: ['contentId', 'simulationPortalId'],
  fetcher({
    contentId,
    simulationPortalId
  }) {
    return http.post(`${EXERCISE_SUBMISSIONS_URI}/${contentId}/asset/automatic`, {
      data: {
        simulationPortalId
      }
    });
  }
});
export const useSetAutomaticallyGradedPracticalExerciseSubmitted = () => {
  const [mutate, {
    loading: submitAutomaticallyGradedPracticalExerciseLoading,
    called: submitAutomaticallyGradedPracticalExerciseCalled,
    error: submitAutomaticallyGradedPracticalExerciseError
  }] = useMutation(SUBMIT_AUTOMATICALLY_GRADED_PRACTICAL_EXERCISE);
  const submitAutomaticallyGradedExercise = useCallback(({
    contentId,
    trackId,
    lessonId,
    simulationPortalId
  }) => {
    return mutate({
      variables: {
        contentId,
        simulationPortalId
      },
      update() {
        setContentProgressStatusCache({
          trackId,
          lessonId,
          contentId,
          status: 'IN_REVIEW'
        });
      }
    }).then(response => response.data);
  }, [mutate]);
  return [submitAutomaticallyGradedExercise, {
    submitAutomaticallyGradedPracticalExerciseLoading,
    submitAutomaticallyGradedPracticalExerciseCalled,
    submitAutomaticallyGradedPracticalExerciseError
  }];
};
const SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE_WITH_ASSET = registerMutation({
  fieldName: 'submitManuallyGradedPracticalExerciseWithAsset',
  args: ['contentId', 'assetType', 'reference'],
  fetcher({
    contentId,
    assetType,
    reference
  }) {
    return http.post(`${EXERCISE_SUBMISSIONS_URI}/${contentId}/manual-with-asset`, {
      data: {
        assetType,
        reference
      }
    });
  }
});
export const useSetManuallyGradedPracticalExerciseWithAssetSubmitted = () => {
  const [mutate, {
    loading: submitManuallyGradedPracticalExerciseWithAssetLoading,
    called: submitManuallyGradedPracticalExerciseWithAssetCalled,
    error: submitManuallyGradedPracticalExerciseWithAssetError
  }] = useMutation(SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE_WITH_ASSET);
  const submitPracticalExerciseWithAsset = useCallback(({
    contentId,
    assetType,
    reference,
    trackId,
    lessonId
  }) => {
    return mutate({
      variables: {
        contentId,
        assetType,
        reference
      },
      update() {
        setContentProgressStatusCache({
          trackId,
          lessonId,
          contentId,
          status: 'IN_REVIEW'
        });
      }
    }).then(response => response.data);
  }, [mutate]);
  return [submitPracticalExerciseWithAsset, {
    submitManuallyGradedPracticalExerciseWithAssetLoading,
    submitManuallyGradedPracticalExerciseWithAssetCalled,
    submitManuallyGradedPracticalExerciseWithAssetError
  }];
};
export const usePracticalExerciseSimulationPortal = ({
  variables
}) => {
  const [mutate, {
    data: simulationPortal,
    loading: simulationPortalLoading,
    error: simulationPortalError
  }] = useMutation(CREATE_PORTAL_MUTATION, {
    variables
  });
  return {
    getOrCreateSimulationPortal: mutate,
    simulationPortal,
    simulationPortalLoading,
    simulationPortalError
  };
};

// Track IDs that are fully enabled (no gate)
// Sales Hub Cert, Marketing Hub Cert, Hubspot Reporting, Email Marketing, Set up Hubspot CRM for Growth, Service Hub Software, Email Marketing Software, Hubspot Content Hub for Marketers, Using Hubspot's Free Marketing Tools
const ENABLED_SIMULATION_TRACKS = {
  qa: [35515440, 65995930, 1097271, 1097331, 88515713, 94508094],
  prod: [9147486, 9219692, 9181275, 9151241, 8984877, 9148739, 9219097, 9206068, 9023279]
};

// Exercise IDs that are fully enabled (no gate)
// [Advance a Deal on Your Deal Board in HubSpot, Create a Task From a Contact Record, Book a Meeting Using the Meetings Tool]
const ENABLED_SIMULATION_EXERCISES = {
  qa: [22745667, 22745653, 22745655, 103330727],
  prod: [8251321, 14672225, 8251397]
};

// Exercise IDs that should NOT open in simulation mode (blacklist)
// 8171535: Make a call, Make a call in Hubspot
const DISABLED_SIMULATION_EXERCISES = {
  qa: [],
  prod: [8171535, 14756457]
};
export const useIsSimulationExercise = (exerciseId, trackId) => {
  const environment = enviro.isQa() ? 'qa' : 'prod';
  const isDisabledSimulationExercise = DISABLED_SIMULATION_EXERCISES[environment].includes(exerciseId);
  const isEnabledSimulationTrack = trackId ? ENABLED_SIMULATION_TRACKS[environment].includes(trackId) : false;
  const isEnabledSimulationExercise = ENABLED_SIMULATION_EXERCISES[environment].includes(exerciseId);
  const {
    hasGate: hasAllExercisesGate,
    hasGateLoading: isAllExercisesGateLoading
  } = useHasGate('Academy:Simulation:AllExercises');
  const isSimulationExercise = useMemo(() => {
    if (isDisabledSimulationExercise) return false;
    if (isEnabledSimulationTrack) return true;
    if (isEnabledSimulationExercise) return true;
    if (hasAllExercisesGate) return true;
    return false;
  }, [isDisabledSimulationExercise, isEnabledSimulationTrack, isEnabledSimulationExercise, hasAllExercisesGate]);
  const isSimulationGateLoading = isEnabledSimulationTrack || isEnabledSimulationExercise ? false : isAllExercisesGateLoading;
  return {
    isSimulationExercise,
    isSimulationGateLoading
  };
};
export const usePracticalExercisePortalId = (exerciseId, trackId) => {
  const currentPortalId = PortalIdParser.getPortalIdFromPath();
  const {
    isSimulationExercise,
    isSimulationGateLoading
  } = useIsSimulationExercise(exerciseId, trackId);
  const {
    getOrCreateSimulationPortal,
    simulationPortal,
    simulationPortalLoading,
    simulationPortalError
  } = usePracticalExerciseSimulationPortal({
    variables: {
      exerciseId
    }
  });
  const getOrCreatePortal = useCallback(() => {
    if (isSimulationExercise) {
      return getOrCreateSimulationPortal();
    }
    return undefined;
  }, [isSimulationExercise, getOrCreateSimulationPortal]);
  return {
    getOrCreatePortal,
    portalId: isSimulationExercise ? (simulationPortal === null || simulationPortal === void 0 ? void 0 : simulationPortal.portalId) || currentPortalId : currentPortalId,
    portalIdLoading: simulationPortalLoading,
    portalIdError: simulationPortalError,
    isSimulationExercise,
    isSimulationGateLoading
  };
};